import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicShell from "../components/PublicShell";
import { Button } from "../components/ui/button";
import {
  Globe2,
  Gavel,
  Sparkles,
  Trophy,
  Building2,
  GraduationCap,
  Users,
  Briefcase,
  ArrowRight,
  Check,
} from "lucide-react";

const SOLUTIONS = [
  {
    icon: Globe2,
    eyebrow: "Diplomacy",
    title: "Model United Nations",
    blurb:
      "From UNSC crisis simulations to 1500-delegate flagship MUNs — councils, country allotment, position papers, live voting and chair scoring in one platform.",
    points: [
      "Council & country matrix with bloc visualisation",
      "Position paper auto-evaluation with Diplomexa AI",
      "Roll-call, secret ballot & two-thirds majority engines",
    ],
    img: "https://images.unsplash.com/photo-1725618878496-233974f2fd59",
  },
  {
    icon: Gavel,
    eyebrow: "Discourse",
    title: "Debate Championships",
    blurb:
      "Asian Parliamentary, British Parliamentary, World Schools — fixture trees, judge ballots, speaker scores and break-round automation.",
    points: [
      "Adjudicator ballots with confidential scoring",
      "Power-pairing & break-round generator",
      "Speaker tabs published the moment a round ends",
    ],
    img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7",
  },
  {
    icon: Sparkles,
    eyebrow: "Civic action",
    title: "Youth Parliaments",
    blurb:
      "Lok Sabha, US Congress, EU Parliament, Westminster — bill drafting, division votes, party whip workflows and televised cabinet feel.",
    points: [
      "Bill versioning with co-sponsor pipeline",
      "Division voting with party-wise breakdown",
      "Cabinet, opposition & speaker dashboards",
    ],
    img: "https://images.unsplash.com/photo-1528747045269-390fe33c19f2",
  },
  {
    icon: Trophy,
    eyebrow: "Sustainability",
    title: "SDG & Climate Summits",
    blurb:
      "Sustainable development goal-led summits with live country pledges, indicator tracking and impact dashboards.",
    points: [
      "17 SDG-aligned committee templates",
      "Live indicator dashboards visible to attendees",
      "Pledge ledger published as a public verification page",
    ],
    img: "https://images.unsplash.com/photo-1542611063-6d0a91dd9eed",
  },
  {
    icon: GraduationCap,
    eyebrow: "Leadership",
    title: "Leadership & Innovation Summits",
    blurb:
      "Hackathons, founder bootcamps, leadership immersions — workshop scheduling, pitch judging and skill scorecards.",
    points: [
      "Multi-track scheduling with conflict detection",
      "Judge panels with live pitch scoring",
      "AI leadership scorecard handed to every delegate",
    ],
    img: "https://images.unsplash.com/photo-1556761175-b413da4baf72",
  },
  {
    icon: Building2,
    eyebrow: "Institutions",
    title: "School & University Conferences",
    blurb:
      "Annual interschool meets, intra-university summits, alumni reunions — branded microsites with multi-event seasons under one umbrella.",
    points: [
      "White-label microsite per conference",
      "Multi-event organisation seasons & passes",
      "Coordinator dashboards for school principals",
    ],
    img: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1",
  },
];

const STAKEHOLDERS = [
  {
    icon: Users,
    title: "For Delegates",
    points: [
      "Curated pre-MUN LMS with quizzes & badges",
      "AI prep assistant that knows your country & agenda",
      "Lifelong digital passport with awards & skills",
    ],
  },
  {
    icon: Gavel,
    title: "For Chairs & EBs",
    points: [
      "Live session OS — speaker list, motions, timers, scores",
      "Crisis updates pushed instantly to delegates",
      "AI award recommendations to support fair decisions",
    ],
  },
  {
    icon: Briefcase,
    title: "For Organisers",
    points: [
      "Conference health score & live registration funnel",
      "Built-in CRM, broadcasts & sponsor portal",
      "Investor-grade analytics with a single export",
    ],
  },
  {
    icon: Building2,
    title: "For Schools",
    points: [
      "Coordinator dashboard for the entire delegation",
      "Global Exposure Report ready to share with parents",
      "Bulk invoices, certificates & impact reporting",
    ],
  },
];

export default function SolutionsPage() {
  return (
    <PublicShell>
      <section className="relative overflow-hidden bg-brand-navy-dark text-brand-champagne">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542611063-6d0a91dd9eed')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark via-brand-navy/95 to-brand-navy-dark/80" />
        <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
        <div aria-hidden className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28">
          <p className="eyebrow text-brand-gold">
            Solutions
          </p>
          <h1 className="mt-4 headline-luxury text-4xl sm:text-5xl lg:text-[4.5rem] text-brand-champagne max-w-4xl">
            One operating system.{" "}
            <span className="italic font-display font-medium text-brand-gold">Every kind</span> of conference.
          </h1>
          <div className="divider-gold-hairline mt-8 max-w-md" />
          <p className="mt-8 text-base lg:text-lg text-brand-champagne/70 max-w-2xl leading-relaxed">
            Whether you&apos;re running a 200-delegate intraschool MUN or a 5000-delegate
            global youth diplomacy summit, Diplomexa adapts to your format —
            never the other way around.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-8">
          {SOLUTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white"
                data-testid={`solution-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="h-44 bg-cover bg-center relative" style={{ backgroundImage: `url(${s.img})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 to-transparent" />
                  <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                        {s.eyebrow}
                      </p>
                      <h3 className="mt-1 text-2xl font-bold tracking-tight text-white">
                        {s.title}
                      </h3>
                    </div>
                    <div className="h-10 w-10 rounded-md bg-[#D4AF37] text-[#0A192F] flex items-center justify-center">
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-slate-600 leading-relaxed">{s.blurb}</p>
                  <ul className="mt-5 space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      <section className="bg-[#f6f8fb] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Built for every stakeholder
            </p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Five dashboards. <span className="font-serif italic text-[#0F4C81]">One source of truth.</span>
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STAKEHOLDERS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-xl bg-white border border-slate-200 p-6">
                  <div className="h-10 w-10 rounded-md bg-[#0A192F] text-[#D4AF37] flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold tracking-tight text-[#0A192F]">{s.title}</h3>
                  <ul className="mt-3 space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="mt-1.5 h-1 w-1 rounded-full bg-[#D4AF37]" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#0A192F]" />
        <div className="relative max-w-5xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tighter text-white">
            Looking for something <span className="gold-gradient-text font-serif italic">specific</span>?
          </h2>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto">
            Our team has helped run MUNs in 18 countries, debate circuits across 4
            continents and youth parliaments for 60+ schools. If you can imagine the
            format, we've probably already shipped a workflow for it.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/demo">
              <Button className="h-12 px-6 bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]" data-testid="solutions-demo-cta">
                Book a tailored demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="h-12 px-6 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="solutions-pricing-cta">
                See pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
